import React, { ReactElement, useEffect, useMemo } from "react";
import { blue, gray } from "../../theme";
import { Toggle } from "@kunduz/ui-atoms";
import { type IContent } from "@kunduz/data-access-cms-api";
import { StickyGradeCarousel } from "./stickyGradeCarousel";
import { trackEventNext } from "@kunduz/utility-analytics";
import styles from "./segmentedPricing.module.scss";
import { PlanV2Card } from "./planV2";
// import { SpeculationRules } from "@kunduz/feature-website-common";

export const SegmentedPricing = (props: Props): ReactElement => {
  const filterPriceGroup = (
    _periodGroups: Props["periodGroups"],
    _gradeGroups: Props["gradeGroups"],
    _grade: Props["grade"],
    _index: number,
  ): Props["periodGroups"][0] => {
    if (_gradeGroups && _gradeGroups.length > 0) {
      const _gradesYearlyPeriod = _gradeGroups.find((g) => g.id === _grade)
        ?.period_groups[_index];
      if (_gradesYearlyPeriod !== undefined) {
        return _gradesYearlyPeriod;
      }
    }
    return _periodGroups[_index] as Props["periodGroups"][0];
  };

  const yearlyPriceGroup: Props["periodGroups"][0] = useMemo(
    () =>
      filterPriceGroup(props.periodGroups, props.gradeGroups, props.grade, 0),
    [props.periodGroups, props.gradeGroups, props.grade],
  );
  const monthlyPriceGroup: Props["periodGroups"][0] = useMemo(
    () =>
      filterPriceGroup(props.periodGroups, props.gradeGroups, props.grade, 1),
    [props.periodGroups, props.gradeGroups, props.grade],
  );

  useEffect(() => {
    props.setPeriodType(yearlyPriceGroup.id);
  }, [yearlyPriceGroup, monthlyPriceGroup, props.grade]);

  const filterPlans = (
    _plans: IContent.PricingComponentData["prices"],
    _pricingType: number,
    _grade: number,
  ) => {
    if (!_plans) return [];

    const filteredPlans = _plans.filter(
      (p) => p.grade_group == props.grade && p.period_group == props.periodType,
    );
    return filteredPlans;
  };
  const memoizedPlans = useMemo(
    () => filterPlans(props.plans, props.periodType, props.grade as number),
    [props.plans, props.periodType, props.grade],
  );

  useEffect(() => {
    if (!memoizedPlans) return;
    props.handleViewEvent(memoizedPlans);
  }, [memoizedPlans]);

  // const calculatePrerenderUrls = (
  //   _memoizedPlans: IContent.PlanV2CardType[],
  // ): string[] => {
  //   const urls: string[] = [];

  //   _memoizedPlans.forEach((x) => {
  //     x.primary_cta_title && urls.push(x.primary_cta_link);
  //     x.secondary_cta_title && urls.push(x.secondary_cta_link);
  //   });

  //   return urls;
  // };
  // const memoizedPrerenderUrls = useMemo(
  //   () => calculatePrerenderUrls(memoizedPlans),
  //   [memoizedPlans],
  // );

  const handleSecondaryButtonClicked = (e: any) => {
    trackEventNext({
      event: "Package Detail Button Clicked",
      label: e.target.text,
    });
  };
  const handlePrimaryButtonClicked = (e: any) => {
    trackEventNext({
      event: "Package Purchase Button Clicked",
      label: e.target.text,
      redirect_url: e.target.href,
    });
  };
  const handleVideoClicked = (name: string, id: string) => {
    trackEventNext({
      event: "Package Card Video Started",
      package_price_name: name,
      package_price_id: id,
    });
  };
  const BadgeIcon = props.badgeIcon;

  return (
    <>
      {/* <SpeculationRules prerender={memoizedPrerenderUrls} /> */}
      <section className={styles.base}>
        <div className={styles.container}>
          <div className={styles.headerSection}>
            <div className={styles.badgeAndTitle}>
              {props.badgeText && (
                <div className={styles.badge}>
                  {!!BadgeIcon && <BadgeIcon className={styles.badgeIcon} />}
                  <p className={styles.badgeText}>{props.badgeText}</p>
                </div>
              )}
              <p className={styles.title}>{props.title}</p>
            </div>
            <p className={styles.supportText}>{props.desc}</p>
          </div>
        </div>
      </section>

      {!props.hideGradeGroups && (
        <StickyGradeCarousel
          gradeGroups={props.gradeGroups}
          grade={props.grade}
          setGrade={props.setGrade}
        />
      )}

      <section className={styles.base2}>
        <div className={styles.container}>
          {yearlyPriceGroup && monthlyPriceGroup && !props.hidePeriodGroups && (
            <div className={styles.headerSection} style={{ paddingTop: 24 }}>
              <div className={styles.toggleLine}>
                <label htmlFor="yearly" className={styles.toogleText}>
                  <span
                    style={{
                      color:
                        props.periodType == monthlyPriceGroup.id
                          ? blue[900]
                          : gray[400],
                    }}
                  >
                    {monthlyPriceGroup.name}
                  </span>{" "}
                </label>
                <Toggle
                  id="yearly"
                  name="yearly"
                  checked={props.periodType == yearlyPriceGroup.id}
                  onChange={(e) =>
                    props.setPeriodType(
                      e.target.checked
                        ? yearlyPriceGroup.id || 1
                        : monthlyPriceGroup.id || 2,
                    )
                  }
                  autoComplete="off"
                />
                <label htmlFor="yearly" className={styles.toogleText}>
                  <span
                    style={{
                      color:
                        props.periodType == yearlyPriceGroup.id
                          ? blue[900]
                          : gray[400],
                    }}
                  >
                    {yearlyPriceGroup.name}
                  </span>{" "}
                  <span
                    style={{
                      color:
                        props.periodType == yearlyPriceGroup.id
                          ? blue[700]
                          : gray[400],
                    }}
                  >
                    {props.priceSwitchText}
                  </span>
                </label>
              </div>
            </div>
          )}
          <div
            className={styles.plans}
            style={{
              justifyContent: "center",
            }}
          >
            {memoizedPlans.map((p, i) => (
              <div
                key={`${p.grade_group}-${p.period_group}-${i}`}
                className={styles.planWrapper}
              >
                <PlanV2Card
                  discountBadge={p.discount_badge}
                  featureBadge={p.meta_option?.badge_text}
                  rating={p.meta_option?.rating}
                  reviewText={p.meta_option?.review}
                  diagonalBadge={p.diagonal_badge}
                  fullPrice={p.full_price}
                  highlightText={p.highlight_text}
                  monthlyPrice={p.monthly_price}
                  packageName={p.meta_option?.title}
                  period={p.period}
                  primaryCtaLabel={p.primary_cta_title}
                  primaryCtaUrl={p.primary_cta_link}
                  primaryCtaType={p.primary_cta_type}
                  onPrimaryCtaClicked={handlePrimaryButtonClicked}
                  secondaryCtaLabel={p.secondary_cta_title}
                  secondaryCtaUrl={p.secondary_cta_link}
                  secondaryCtaType={p.secondary_cta_type}
                  onSecondaryCtaClicked={handleSecondaryButtonClicked}
                  colorTheme={(p.bg_color || "blue") as any}
                  summaryOptions={p.summary_option}
                  videoId={p.wistia_video_id}
                  packageId={p.package_id}
                  packagePriceId={p.package_price_id}
                  onClick={handleVideoClicked}
                />
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

interface Props {
  badgeIcon: React.ElementType;
  badgeText: string;
  title: string;
  desc: string;
  priceSwitchText: string;
  periodGroups: {
    id: number;
    name: string;
    highlight_text_color: string;
  }[];
  gradeGroups: {
    id: number;
    name: string;
    period_groups: {
      id: number;
      name: string;
      highlight_text_color: string;
    }[];
  }[];
  plans: IContent.PricingComponentData["prices"];
  benefitTitle: string;
  grade: number;
  setGrade: (_g: number) => void;
  periodType: number;
  setPeriodType: (_g: number) => void;
  hidePeriodGroups?: boolean;
  hideGradeGroups?: boolean;
  handleViewEvent: (plans: IContent.PlanV2CardType[]) => void;
}
